/* ── Player Career Section ─────────────────────────────────────────────────
   Reusable component used on both PlayerDashboard (authenticated) and
   PublicPlayerDashboard (public profile).

   Props
   ─────
   seasons      – array of season rows, each shaped as:
                  {
                    id,          // player row id
                    season_id,
                    seasons: { name },
                    teams:   { name, primary_color, secondary_color, gradient }
                  }
   seasonStats  – object keyed by season_id:
                  { gp, pts, reb, ast, three_pm, two_pm, ft_made }
   loading      – boolean
   emptyAction  – optional ReactNode rendered below the empty-state message
                  (e.g. a "Claim a Past Season" button on the auth dashboard)
   ──────────────────────────────────────────────────────────────────────── */
import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  getTeamStyle,
  getTextColorForAccent,
  isDarkModeActive,
} from "../utils/teamColors";

/* ── Team badge rendered in the team's own colors ─────────────────────── */
const TeamBadge = ({ team }) => {
  if (!team?.name) return null;
  const style = getTeamStyle(team);
  const textColor = getTextColorForAccent(style.primaryHex, isDarkModeActive());
  return (
    <span
      className="inline-flex items-center rounded-full font-semibold text-xs px-3 py-1 shrink-0"
      style={{ ...style.headerStyle, color: textColor }}
    >
      {team.name}
    </span>
  );
};

/* ── Stat cell ────────────────────────────────────────────────────────── */
const StatCell = ({ label, value, highlight = false }) => (
  <div className="flex flex-col items-center gap-0.5">
    <span
      className={`text-lg font-bold leading-none tabular-nums ${
        highlight ? "text-orange-600 dark:text-orange-400" : "dark:text-white"
      }`}
    >
      {value}
    </span>
    <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
      {label}
    </span>
  </div>
);

/* ── Divider between stat groups ──────────────────────────────────────── */
const StatDivider = () => (
  <div className="w-px h-8 bg-gray-100 dark:bg-gray-800 self-center" />
);

/* ── Single season row ────────────────────────────────────────────────── */
const SeasonRow = ({ player, stats }) => {
  const team = player.teams;
  const seasonName = player.seasons?.name ?? "—";

  const gp = stats?.gp ?? 0;
  const pts = stats?.pts ?? 0;
  const reb = stats?.reb ?? 0;
  const ast = stats?.ast ?? 0;
  const threePm = stats?.three_pm ?? 0;
  const twoPm = stats?.two_pm ?? 0;
  const ftm = stats?.ft_made ?? 0;

  const ppg = gp > 0 ? (pts / gp).toFixed(1) : "—";
  const rpg = gp > 0 ? (reb / gp).toFixed(1) : "—";
  const apg = gp > 0 ? (ast / gp).toFixed(1) : "—";

  const hasStats = gp > 0;

  return (
    <div className="rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
      {/* Season header bar */}
      <div className="flex items-center justify-between px-5 py-3 bg-gray-50 dark:bg-gray-800/50 gap-3 flex-wrap">
        <span className="font-semibold text-sm dark:text-white tracking-tight">
          {seasonName}
        </span>
        <TeamBadge team={team} />
      </div>

      {/* Stats body */}
      {hasStats ? (
        <div className="px-5 py-4">
          {/* Averages row */}
          <div className="flex items-center justify-around gap-1 mb-4">
            <StatCell label="GP" value={gp} highlight />
            <StatDivider />
            <StatCell label="PPG" value={ppg} highlight />
            <StatDivider />
            <StatCell label="RPG" value={rpg} highlight />
            <StatDivider />
            <StatCell label="APG" value={apg} highlight />
          </div>

          {/* Totals row */}
          <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
            {[
              ["PTS", pts],
              ["REB", reb],
              ["AST", ast],
              ["3PM", threePm],
              ["2PM", twoPm],
              ["FTM", ftm],
            ].map(([label, val]) => (
              <div key={label} className="flex flex-col items-center gap-0.5">
                <span className="text-sm font-bold tabular-nums dark:text-white">
                  {val}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="px-5 py-4 text-sm text-gray-400 dark:text-gray-600 italic">
          No stats recorded this season.
        </p>
      )}
    </div>
  );
};

/* ── Skeleton loader ──────────────────────────────────────────────────── */
const CareerSkeleton = () => (
  <div className="space-y-4">
    {[1, 2].map((i) => (
      <div
        key={i}
        className="h-36 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse"
      />
    ))}
  </div>
);

/* ── Main exported component ──────────────────────────────────────────── */
const PlayerCareerSection = ({
  seasons = [],
  seasonStats = {},
  loading = false,
  emptyAction = null,
}) => {
  const [showAll, setShowAll] = useState(false);
  const INITIAL_SHOW = 3;

  if (loading) return <CareerSkeleton />;

  if (seasons.length === 0) {
    return (
      <div className="text-center py-10 rounded-2xl border border-dashed border-gray-200 dark:border-gray-800">
        <p className="text-gray-500 dark:text-gray-400 mb-1">
          No seasons on record yet.
        </p>
        {emptyAction && (
          <div className="mt-3 text-sm text-gray-400 dark:text-gray-500">
            {emptyAction}
          </div>
        )}
      </div>
    );
  }

  const visible = showAll ? seasons : seasons.slice(0, INITIAL_SHOW);
  const hiddenCount = seasons.length - INITIAL_SHOW;

  return (
    <div className="space-y-3">
      {visible.map((p) => (
        <SeasonRow key={p.id} player={p} stats={seasonStats[p.season_id]} />
      ))}

      {seasons.length > INITIAL_SHOW && (
        <button
          onClick={() => setShowAll((v) => !v)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-gray-100 dark:border-gray-800 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        >
          {showAll ? (
            <>
              <ChevronUp size={15} /> Show less
            </>
          ) : (
            <>
              <ChevronDown size={15} /> Show {hiddenCount} more season
              {hiddenCount !== 1 ? "s" : ""}
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default PlayerCareerSection;
